import {
  SearchAdminInvoiceReportsQueryPaymentMethod,
  SearchInvoiceReportsQueryMfFile,
  SearchInvoiceReportsQueryPaymentStatus,
  SearchInvoiceReportsQueryStatus,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';
import {
  useGetDeptsData,
  useGetOfficeInvoiceReportSettings,
  useGetOfficeMembersData,
} from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useGetEDocPlan } from '@/context/services/e_doc_plan/EDocPlan.service';

import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { useEffect, useMemo, useRef, useState } from 'react';

const useInitialData = <T extends BaseOption>(
  ids: string[],
  fetchFunction: (ids: string[]) => Promise<T[]>
) => {
  const [options, setOptions] = useState<T[]>([]);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      if (ids.length === 0) {
        setOptions([]);
        return;
      }
      const fetchData = async () => {
        try {
          const data = await fetchFunction(ids);
          setOptions(data);
        } catch {
          setOptions([]);
        }
      };
      fetchData();
    }
  }, [ids, fetchFunction]);

  return { options };
};

export const useOfficeMembersInitialData = (memberIds: string[]) => {
  const { fetchOfficeMembers } = useGetOfficeMembersData();
  const { options } = useInitialData(memberIds, fetchOfficeMembers);
  return options;
};

export const useDeptInitialData = (deptIds: string[]) => {
  const { fetchDepts } = useGetDeptsData();
  const { options } = useInitialData(deptIds, fetchDepts);
  return options;
};

export const useConfigForm = () => {
  const { t } = useTranslation();
  const { isInvoiceReportDocuments } = useGetEDocPlan();
  const { data: officeSettings } = useGetOfficeInvoiceReportSettings();
  const deptsExisted = officeSettings?.data?.depts_existed;
  const reportDeptsSetting = officeSettings?.data?.report_depts_setting;

  const statusOptions = useMemo(
    () => [
      {
        label: t('admin_invoice_reports.search.status.unsubmitted'),
        value: SearchInvoiceReportsQueryStatus.unsubmitted,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_all'),
        value: SearchInvoiceReportsQueryStatus.waiting_all,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_one'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_one,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_two'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_two,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_three'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_three,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_four'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_four,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_five'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_five,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_six'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_six,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_seven'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_seven,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_eight'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_eight,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_nine'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_nine,
      },
      {
        label: t('admin_invoice_reports.search.status.waiting_step_ten'),
        value: SearchInvoiceReportsQueryStatus.waiting_step_ten,
      },
      {
        label: t('admin_invoice_reports.search.status.approved'),
        value: SearchInvoiceReportsQueryStatus.approved,
      },
      {
        label: t('admin_invoice_reports.search.status.canceled'),
        value: SearchInvoiceReportsQueryStatus.canceled,
      },
      {
        label: t('admin_invoice_reports.search.status.rejected'),
        value: SearchInvoiceReportsQueryStatus.rejected,
      },
    ],
    [t]
  );

  const paymentStatusOptions = useMemo(
    () => [
      {
        label: t('admin_invoice_reports.search.payment_status.not_yet_paid'),
        value: SearchInvoiceReportsQueryPaymentStatus.not_yet_paid,
        tooltip: t(
          'admin_invoice_reports.search.payment_status.not_yet_paid_tooltip'
        ),
      },
      {
        label: t('admin_invoice_reports.search.payment_status.paid'),
        value: SearchInvoiceReportsQueryPaymentStatus.paid,
        tooltip: t('admin_invoice_reports.search.payment_status.paid_tooltip'),
      },
    ],
    [t]
  );

  const mfFileOptions = useMemo(() => {
    const base = [
      {
        label: t('admin_invoice_reports.search.mf_file.not_attached'),
        value: SearchInvoiceReportsQueryMfFile.not_attached,
      },
      {
        label: t('admin_invoice_reports.search.mf_file.attached'),
        value: SearchInvoiceReportsQueryMfFile.attached,
      },
    ];
    if (isInvoiceReportDocuments) {
      return [
        ...base,
        {
          label: t(
            'admin_invoice_reports.search.mf_file.attached_receipt_type_paper'
          ),
          value: SearchInvoiceReportsQueryMfFile.attached_receipt_type_paper,
        },
        {
          label: t(
            'admin_invoice_reports.search.mf_file.attached_receipt_type_e_doc'
          ),
          value: SearchInvoiceReportsQueryMfFile.attached_receipt_type_e_doc,
        },
      ];
    }
    return base;
  }, [t, isInvoiceReportDocuments]);

  const paymentMethodOptions = useMemo(
    () => [
      {
        label: t('admin_invoice_reports.search.payment_method.bank_transfer'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.bank_transfer,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.cash'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.cash,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.payment_slip'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.payment_slip,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.direct_debit'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.direct_debit,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.credit_card'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.credit_card,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.prepaid_card'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.prepaid_card,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.check'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.check,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.promissory_note'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.promissory_note,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.densai'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.densai,
      },
      {
        label: t(
          'admin_invoice_reports.search.payment_method.remittance_abroad'
        ),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.remittance_abroad,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.other'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.other,
      },
    ],
    [t]
  );

  const labels = useMemo(
    () => ({
      query: {
        text: t('admin_invoice_reports.search.keyword'),
        tooltip: t('admin_invoice_reports.search.keyword_tooltip'),
      },
      number: { text: t('admin_invoice_reports.search.number') },
      status: { text: t('admin_invoice_reports.search.status') },
      applicant: { text: t('admin_invoice_reports.search.applicant') },
      applicant_department: {
        text: t('admin_invoice_reports.search.applicant_department'),
      },
      department: { text: t('admin_invoice_reports.search.department') },
      counterparty: { text: t('admin_invoice_reports.search.counterparty') },
      ap_payee: { text: t('admin_invoice_reports.search.ap_payee') },
      payment_method: {
        text: t('admin_invoice_reports.search.payment_method'),
      },
      total_amount: { text: t('admin_invoice_reports.search.total_amount') },
      payment_status: {
        text: t('admin_invoice_reports.search.payment_status'),
      },
      mf_file: { text: t('admin_invoice_reports.search.mf_file') },
      created_date: { text: t('admin_invoice_reports.search.created_date') },
      submitted_date: {
        text: t('admin_invoice_reports.search.submitted_date'),
      },
      approved_date: {
        text: t('admin_invoice_reports.search.approved_date'),
      },
      book_date: { text: t('admin_invoice_reports.search.book_date') },
      due_date: { text: t('admin_invoice_reports.search.due_date') },
      payment_date: { text: t('admin_invoice_reports.search.payment_date') },
      reset_button: t('admin_invoice_reports.search.reset_button'),
      search_button: t('admin_invoice_reports.search.search_button'),
    }),
    [t]
  );

  const selectProps = useMemo(
    () => ({
      size: 'small' as const,
      apPayee: {
        inputPlaceholder: t(
          'admin_invoice_reports.search.ap_payee.placeholder'
        ),
        size: 'small' as const,
      },
      applicant: {
        maxTagCount: 1,
        placeholder: '',
        inputPlaceholder: t(
          'admin_invoice_reports.search.applicant.placeholder'
        ),
        size: 'small' as const,
      },
      applicantDepartment: {
        maxTagCount: 1,
        placeholder: '',
        inputPlaceholder: t(
          'admin_invoice_reports.search.applicant_department.placeholder'
        ),
        size: 'small' as const,
      },
      department: {
        maxTagCount: 1,
        disabled: !reportDeptsSetting?.enabled,
        placeholder: '',
        inputPlaceholder: t(
          'admin_invoice_reports.search.department.placeholder'
        ),
        size: 'small' as const,
      },
      counterparty: {
        placeholder: t('admin_invoice_reports.search.counterparty.placeholder'),
        size: 'small' as const,
        mode: 'multiple' as const,
      },
    }),
    [t, reportDeptsSetting]
  );

  const inputProps = useMemo(
    () => ({
      query: { search: true, size: 'small' as const },
      number: { search: false, size: 'small' as const },
    }),
    []
  );

  const deptDefaultOptions = useMemo(() => {
    const raw = reportDeptsSetting?.enabled
      ? reportDeptsSetting?.blank_report_dept_option
      : undefined;
    return raw ? [{ label: raw.label || '', value: raw.id || '' }] : undefined;
  }, [reportDeptsSetting]);

  return {
    labels,
    selectProps,
    inputProps,
    statusOptions,
    paymentStatusOptions,
    mfFileOptions,
    paymentMethodOptions,
    deptsExisted,
    reportDeptsSetting,
    deptDefaultOptions,
  };
};
